import { useMemo } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import { Chip, FormHelperText, Stack } from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import PropTypes from "prop-types";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import { formatNumber } from "@/features/shared";
import { ProductVariantNameCell, useSnackbar } from "@/features/ui";

import {
  StockAdjustmentTypeEnum,
  StockAdjustmentTypeLabel,
} from "../stock-adjustment-form-constants";

export const StockAdjustmentProductVariantsSection = ({ editMode = false }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const { showSnackbar } = useSnackbar();

  const { update, remove } = useFieldArray({
    control,
    name: "stockAdjustmentProductVariants",
  });

  const productVariants = useWatch({
    control,
    name: "stockAdjustmentProductVariants",
  });

  const handleDelete = (index) => () => {
    remove(index);
    showSnackbar("Produk berhasil dihapus", "success");
  };

  const processRowUpdate = (newRow) => {
    return new Promise((resolve, reject) => {
      const { index } = newRow;
      const quantity = Number(newRow.quantity);
      const costPerUnit = Number(newRow.costPerUnit);
      const type = newRow.type;

      if (!quantity || quantity <= 0) {
        reject(new Error("Kuantitas harus lebih dari 0"));
        return;
      }

      if (
        type === StockAdjustmentTypeEnum.ADDITION &&
        (costPerUnit < 0 || Number.isNaN(costPerUnit))
      ) {
        reject(new Error("Biaya per unit tidak boleh negatif"));
        return;
      }

      const updated = {
        ...productVariants[index],
        type,
        quantity,
        costPerUnit:
          type === StockAdjustmentTypeEnum.DEDUCTION ? 0 : costPerUnit,
        note: newRow.note ?? "",
      };

      update(index, updated);
      resolve({ ...newRow, ...updated });
    });
  };

  const handleProcessRowUpdateError = (error) => {
    showSnackbar(error.message, "error");
  };

  const rows = useMemo(
    () =>
      (productVariants || []).map((line, index) => ({
        ...line,
        index,
      })),
    [productVariants],
  );

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Nama",
        flex: 3,
        sortable: false,
        renderCell: ({ row: { name, sku } }) => (
          <ProductVariantNameCell name={name} sku={sku} />
        ),
      },
      { field: "unitOfMeasurement", headerName: "Unit", width: 80 },
      {
        field: "type",
        headerName: "Tipe",
        width: 140,
        sortable: false,
        editable: !editMode,
        type: "singleSelect",
        valueOptions: [
          {
            value: StockAdjustmentTypeEnum.ADDITION,
            label: StockAdjustmentTypeLabel[StockAdjustmentTypeEnum.ADDITION],
          },
          {
            value: StockAdjustmentTypeEnum.DEDUCTION,
            label: StockAdjustmentTypeLabel[StockAdjustmentTypeEnum.DEDUCTION],
          },
        ],
        renderCell: ({ row }) => (
          <Chip
            size="small"
            color={
              row.type === StockAdjustmentTypeEnum.ADDITION
                ? "success"
                : "warning"
            }
            label={StockAdjustmentTypeLabel[row.type]}
          />
        ),
      },
      {
        field: "quantity",
        headerName: "Qty",
        flex: 1,
        type: "number",
        valueFormatter: (value) => formatNumber(value),
        editable: true,
        align: "right",
        headerAlign: "right",
      },
      {
        field: "costPerUnit",
        headerName: "Biaya Satuan (Rp)",
        flex: 1.2,
        type: "number",
        editable: true,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => formatNumber(value || 0),
        cellClassName: ({ row }) =>
          row.type === StockAdjustmentTypeEnum.DEDUCTION
            ? "MuiDataGrid-cell--disabled"
            : "",
      },
      {
        field: "note",
        headerName: "Catatan",
        flex: 1.5,
        sortable: false,
        editable: true,
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Aksi",
        getActions: ({ row }) => [
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Hapus"
            onClick={handleDelete(row.index)}
            color="error"
          />,
        ],
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [productVariants, editMode],
  );

  const errorMessage =
    errors.stockAdjustmentProductVariants?.message ||
    errors.stockAdjustmentProductVariants?.root?.message;

  return (
    <Stack spacing={1}>
      <DataGrid
        rows={rows}
        columns={columns}
        getRowHeight={() => "auto"}
        disableRowSelectionOnClick
        disableColumnMenu
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={handleProcessRowUpdateError}
        isCellEditable={(params) => {
          if (
            params.field === "costPerUnit" &&
            params.row.type === StockAdjustmentTypeEnum.DEDUCTION
          ) {
            return false;
          }
          return true;
        }}
        localeText={{
          noRowsLabel: "Belum ada produk. Tambahkan dari bagian di atas.",
        }}
      />
      {errorMessage && <FormHelperText error>{errorMessage}</FormHelperText>}
    </Stack>
  );
};

StockAdjustmentProductVariantsSection.propTypes = {
  editMode: PropTypes.bool,
};
